import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtPct, fmtDate, initials, daysUntil } from "@/lib/format";
import { Search, Plus, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import ProgressBar from "@/components/ProgressBar";

export default function Participants() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await endpoints.participants());
    } catch {
      toast.error("Failed to load participants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter === "behind") list = list.filter((p) => p.behind_flag);
    if (filter === "evaluable") list = list.filter((p) => p.evaluable);
    if (filter === "not_evaluable") list = list.filter((p) => !p.evaluable);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(s) ||
          p.trip_destination.toLowerCase().includes(s) ||
          (p.city || "").toLowerCase().includes(s),
      );
    }
    return list;
  }, [rows, q, filter]);

  return (
    <div className="container-hi py-10" data-testid="participants-page">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
            Participants
          </div>
          <h1 className="font-display text-[44px] leading-[1.05] mt-2" style={{ color: "var(--cream)" }}>
            Cohort roster
          </h1>
          <p className="mt-3 text-[13px]" style={{ color: "var(--cream-dim)" }}>
            {rows.length} recruited · target 30 U.S. adult travelers with genuine leisure trips.
          </p>
        </div>
        <button className="btn-gold inline-flex items-center gap-2" onClick={() => setShowAdd(true)} data-testid="add-participant-btn">
          <Plus className="w-3.5 h-3.5" /> Add participant
        </button>
      </header>

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md w-[320px]"
          style={{ background: "var(--surface)", border: "1px solid var(--hairline)" }}
        >
          <Search className="w-4 h-4" style={{ color: "var(--cream-muted)" }} />
          <input
            data-testid="participants-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, destination, city"
            className="bg-transparent outline-none text-[13px] w-full"
            style={{ color: "var(--cream)" }}
          />
        </div>
        <div className="flex gap-1 rounded-md p-1" style={{ background: "var(--surface)", border: "1px solid var(--hairline)" }}>
          {[
            { k: "all", label: "All" },
            { k: "evaluable", label: "Evaluable" },
            { k: "not_evaluable", label: "Not yet evaluable" },
            { k: "behind", label: "Behind" },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className="px-3 py-1.5 text-[12px] rounded"
              style={{
                background: filter === f.k ? "var(--gold)" : "transparent",
                color: filter === f.k ? "var(--obsidian)" : "var(--cream-dim)",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 surface overflow-hidden" data-testid="participants-table">
        <div
          className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-[0.15em] font-mono-num border-b"
          style={{ color: "var(--cream-muted)", borderColor: "var(--hairline)" }}
        >
          <div className="col-span-3">Participant</div>
          <div className="col-span-3">Trip</div>
          <div className="col-span-2">Adherence</div>
          <div className="col-span-2">Preservation</div>
          <div className="col-span-2 text-right">Goal Progress</div>
        </div>

        {loading && (
          <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>
            Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-6 py-10 text-center text-[13px]" style={{ color: "var(--cream-muted)" }}>
            No participants match this filter.
          </div>
        )}

        {filtered.map((p) => {
          const goalPct = p.savings_goal ? p.travel_goal_progress / p.savings_goal : 0;
          const trip = daysUntil(p.trip_date);
          return (
            <Link
              to={`/participants/${p.id}`}
              key={p.id}
              className="grid grid-cols-12 px-6 py-4 items-center border-b row-hover"
              style={{ borderColor: "var(--hairline)" }}
              data-testid={`participant-row-${p.id}`}
            >
              <div className="col-span-3 flex items-center gap-3 min-w-0">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-mono-num shrink-0"
                  style={{ background: "var(--surface-3)", color: "var(--gold-light)", border: "1px solid var(--hairline-strong)" }}
                >
                  {initials(p.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] truncate flex items-center gap-2" style={{ color: "var(--cream)" }}>
                    {p.name}
                    {p.behind_flag && <AlertTriangle className="w-3.5 h-3.5" style={{ color: "var(--warn)" }} />}
                  </div>
                  <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
                    {p.city || "—"} · {p.cadence}
                  </div>
                </div>
              </div>
              <div className="col-span-3 min-w-0">
                <div className="text-[13px] truncate" style={{ color: "var(--cream)" }}>
                  {p.trip_destination}
                </div>
                <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
                  {fmtDate(p.trip_date)}
                  {trip !== null && trip >= 0 && <span> · {trip}d out</span>}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[14px] font-mono-num" style={{ color: "var(--cream)" }}>
                  {fmtPct(p.adherence_rate)}
                </div>
                <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
                  {p.commitments_on_time}/{p.commitments_due} on time
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[14px] font-mono-num" style={{ color: "var(--cream)" }}>
                  {p.evaluable ? fmtPct(p.preservation_rate) : <span style={{ color: "var(--cream-muted)" }}>pre-eval</span>}
                </div>
                <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
                  {fmtMoney(p.preserved_savings)} preserved
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-right text-[12px] font-mono-num mb-1.5" style={{ color: "var(--cream-dim)" }}>
                  {fmtMoney(p.travel_goal_progress)} / {fmtMoney(p.savings_goal)}
                </div>
                <ProgressBar value={p.travel_goal_progress} max={p.savings_goal} tone={goalPct >= 0.9 ? "success" : "gold"} />
              </div>
            </Link>
          );
        })}
      </div>

      {showAdd && <AddParticipantModal onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}

function AddParticipantModal({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "",
    email: "",
    city: "",
    trip_destination: "",
    trip_date: "",
    savings_goal: 2500,
    starting_designated_amount: 200,
    cadence: "weekly",
    commitment_amount: 100,
    activation_date: today,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await endpoints.createParticipant({
        ...form,
        savings_goal: Number(form.savings_goal),
        starting_designated_amount: Number(form.starting_designated_amount),
        commitment_amount: Number(form.commitment_amount),
      });
      toast.success("Participant added");
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to add participant");
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, type = "text", extra = {}) => (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
        {label}
      </span>
      <input
        data-testid={`field-${k}`}
        type={type}
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="px-3 py-2 rounded-md text-[13px] bg-transparent"
        style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
        required
        {...extra}
      />
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(10,10,12,0.7)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="add-participant-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="surface w-full max-w-2xl p-8 rounded-lg"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
              New participant
            </div>
            <div className="font-display text-[24px] mt-1" style={{ color: "var(--cream)" }}>
              Add to the Stage 1 cohort
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="add-modal-close">
            <X className="w-4 h-4" style={{ color: "var(--cream-dim)" }} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field("name", "Full name")}
          {field("email", "Email", "email")}
          {field("city", "City / state")}
          {field("trip_destination", "Trip destination")}
          {field("trip_date", "Trip date", "date")}
          {field("activation_date", "Activation date", "date")}
          {field("savings_goal", "Savings goal (USD)", "number", { min: 100 })}
          {field("starting_designated_amount", "Starting designated ($)", "number", { min: 0 })}
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
              Cadence
            </span>
            <select
              data-testid="field-cadence"
              value={form.cadence}
              onChange={(e) => setForm({ ...form, cadence: e.target.value })}
              className="px-3 py-2 rounded-md text-[13px]"
              style={{ background: "var(--surface-2)", border: "1px solid var(--hairline-strong)", color: "var(--cream)" }}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly</option>
            </select>
          </label>
          {field("commitment_amount", "Commitment amount ($)", "number", { min: 1 })}
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-gold" disabled={saving} data-testid="add-modal-submit">
            {saving ? "Saving…" : "Save participant"}
          </button>
        </div>
      </form>
    </div>
  );
}
